import { JSX, ReactNode } from "react";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import React from "react";
import { MenuItem } from "./MenuItem";
import "./Styles.scss";

export class Menu extends BaseComponent<IMenuProps, IMenuState>
{
    private menuRef: React.RefObject<HTMLUListElement | null> = React.createRef();

    public componentDidMount(): void
    {
        this.checkChildren();
        this.handleAnchorChange();
    }

    public componentDidUpdate(prevProps: IMenuProps): void
    {
        if (prevProps.children != this.props.children)
        {
            this.checkChildren();
        }
        if (prevProps.anchor != this.props.anchor)
        {
            this.handleAnchorChange();
        }
        if (!prevProps.open && this.props.open)
        {
            this.handleOpen();
        }
    }

    public checkChildren(): void
    {
        const children = React.Children.toArray(this.props.children);
        const childrenAreValid = children.every(i => React.isValidElement(i) && i.type === MenuItem);

        if (!childrenAreValid)
        {
            throw new Error('Menu must contain only MenuItems.');
        }
    }

    private handleAnchorChange(): void
    {
        this.setPosition();
    }

    private handleOpen(): void
    {
        this.setPosition();
    }

    private setPosition(): void
    {
        const menu = this.menuRef.current;
        if (!menu)
        {
            return;
        }

        if (this.props.anchor)
        {
            const margin = 8;
            const rect = this.props.anchor.getBoundingClientRect();
            const menuRect = menu.getBoundingClientRect();

            // Prefer below the anchor, but flip above when it would overflow the
            // bottom of the viewport (and there is more room above).
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            let top = rect.bottom;
            if (spaceBelow < menuRect.height + margin && spaceAbove > spaceBelow)
            {
                top = rect.top - menuRect.height;
            }
            top = Math.max(margin, Math.min(top, window.innerHeight - menuRect.height - margin));

            // Clamp horizontally so the menu stays within the viewport.
            let left = rect.left;
            left = Math.max(margin, Math.min(left, window.innerWidth - menuRect.width - margin));

            menu.style.top = `${top}px`;
            menu.style.left = `${left}px`;
        }
        else
        {
            menu.style.top = '';
            menu.style.left = '';
        }
    }

    public render(): JSX.Element
    {
        const cssClasses: string[] = [];
        cssClasses.push('context-menu');
        cssClasses.push(this.props.className);
        cssClasses.push(this.props.open ? 'context-menu--open' : '');

        return (
            <div
                className={cssClasses.filter(i => !!i).join(' ')}
            >
                <div
                    className='context-menu__mask'
                    onClick={() => this.props.onClose()}
                />
                <ul
                    ref={this.menuRef}
                    className='context-menu__menu'
                >
                    {this.props.children}
                </ul>
            </div>
        );
    }
}

export interface IMenuProps
{
    anchor: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    className: string;
}

export interface IMenuState { }
